/* import __COLOCATED_TEMPLATE__ from './tooltip.hbs.js'; */
var _dec, _dec2, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { guidFor } from '@ember/object/internals';
import { UpsellTooltipImageConfigs, localeForConfig, UPSELL_PROMO_TYPE } from '@dashboard/shared-ui/utils/rst-upsell-config';
import { getUpsellTooltipConfigs as getRetailUpsellTooltipConfigs } from '@dashboard/shared-ui/utils/rtl-upsell-config';
import { Vertical } from '@dashboard/shared-ui/utils/vertical-configs';

/**
  Usage:
    <Upsell::Tooltip @heading="Some Heading"
                     @description="The best description in the world."
                     @vertical={{Vertical.RESTAURANTS}} />

  @param {String} heading
  @param {String} description
  @param {String} vertical - The vertical that is using the tooltip.
                             It configures the brand color shown on the components/svgs/plus-badge-filled
                             Available verticals: ['restaurants', 'retail']
  @param {Action} onLearnMoreClick - Optional argument for an action called when "Learn more" button is clicked. If not provided, it will not display the "Learn more" button.
  @param {UpsellPromoType} promoType The feature the tooltip is promoting
  @param {Event} [upsellBadgeClickEvent] The event to track when the upsell badge is clicked.
  @param {Boolean} [useOutlineBadgeForTooltip] Specifies if the tooltip should use the outlined version of the
    upsell badge.
  @param {String} [placement=top] Determines where the tooltip popover should render relative to the `referenceElement`.
  @param {Boolean} [constrainInParent=true] Specifies if the tooltip should be confined to the parent's frame.
  @param {Boolean} [positionLeft=false] Moves tooltip to the left of the trigger
  @param {Boolean} [positionBottom=false] Moves tooltip to the bottom of the trigger
  @param {String} [outsideClickSelector] Passes through to OutsideClickDetector's selector arg to
                                         customize outside click behavior
 */
let Tooltip = (_dec = computed('centerVertical', 'position', 'positionLeft', 'positionBottom'), _dec2 = computed('promoType', 'userInfo.{browserLanguage,countryCode}', 'vertical'), _class = class Tooltip extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "i18n", _descriptor, this);
    _initializerDefineProperty(this, "intl", _descriptor2, this);
    _initializerDefineProperty(this, "intlCountry", _descriptor3, this);
    _initializerDefineProperty(this, "eventstream", _descriptor4, this);
    _initializerDefineProperty(this, "userInfo", _descriptor5, this);
    // Passed in.
    _defineProperty(this, "constrainInParent", true);
    _defineProperty(this, "placement", 'top');
    _defineProperty(this, "upsellBadgeClickEvent", null);
    _defineProperty(this, "isActive", false);
  }
  // reference for the plus-badge element so the popper can align properly
  get referenceBadgeId() {
    return `upsell-tooltip-reference-badge-${guidFor(this)}`;
  }

  // positions the popper with offset from the badge based on Popper.js modifier rules
  get extraPopperModifiers() {
    return {
      offset: {
        offset: '0,30'
      }
    };
  }
  get popoverClass() {
    if (this.centerVertical) {
      // used by function select because the tooltip is cut off by the modal's edge
      // when centered horizontally over the plus badge
      return 'upsell-tooltip__popover--center-vertical';
    } else if (this.positionLeft) {
      // Useful when the badge is aligned to the right side of the screen
      return 'upsell-tooltip__popover--left';
    } else if (this.positionBottom) {
      // Useful when the badge is aligned to the top side of the screen
      return 'upsell-tooltip__popover--bottom';
    } else {
      // center vertically and horizontally
      return 'upsell-tooltip__popover';
    }
  }
  get imageConfig() {
    if (this.promoType) {
      switch (this.vertical) {
        case Vertical.RESTAURANTS:
        case Vertical.RESTAURANTS_PRO:
          return {
            description: this.promoType === UPSELL_PROMO_TYPE.AUTO_GRATUITY ? this.intlCountry.t(UpsellTooltipImageConfigs[this.promoType]['key']) : this.intl.t(UpsellTooltipImageConfigs[this.promoType]['key']),
            responsiveImageSource: UpsellTooltipImageConfigs[this.promoType][localeForConfig(this.userInfo.countryCode, this.userInfo.browserLanguage)]['src'],
            height: 148,
            width: 280
          };
        case Vertical.RETAIL:
          return getRetailUpsellTooltipConfigs()[this.promoType].imageConfig;
        default:
          return null;
      }
    }
    return null;
  }
  clickBadge() {
    if (this.toggleProperty('isActive') && this.upsellBadgeClickEvent) {
      this.eventstream.track(this.upsellBadgeClickEvent);
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "i18n", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "intl", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "intlCountry", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "eventstream", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "userInfo", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "referenceBadgeId", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "referenceBadgeId"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "extraPopperModifiers", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "extraPopperModifiers"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "popoverClass", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "popoverClass"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "imageConfig", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "imageConfig"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "clickBadge", [action], Object.getOwnPropertyDescriptor(_class.prototype, "clickBadge"), _class.prototype), _class);
export { Tooltip as default };